// src/components/ResultadoLabDialog.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { createLabResult, updateLabResult } from '../infra/api/labResultApi';
import { usePermissao } from '../security/usePermissao';
import { formatarValorLab, parseValorLab, unidadeComparavelComModelo } from '../utils/numeroLab';
import { agruparEntradasLab, normalizarResultadoLab, valoresIniciaisLab } from '../utils/resultadoLab';

/**
 * Lançar/atualizar o resultado de laboratório de um experimento.
 *
 * @param entradas            campos do protocolo (utils/resultadoLab.entradasLabDoProtocolo)
 * @param unidadeComparavel   quais unidades entram na comparação com o modelo; as demais
 *                            são registradas com um aviso. Padrão: NMP (modelo de coliformes).
 * @param onSalvo             recebe o resultado salvo, já normalizado
 */
export default function ResultadoLabDialog({
    open,
    onClose,
    experimentId,
    entradas,
    labResult,
    unidadeComparavel = unidadeComparavelComModelo,
    onSalvo,
}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { pode } = usePermissao();
    const grupos = useMemo(() => agruparEntradasLab(entradas), [entradas]);

    const [valores, setValores] = useState({ analysisDate: '' });
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    useEffect(() => {
        if (!open) return;
        setValores(valoresIniciaisLab(entradas, labResult));
        setErro('');
    }, [open, entradas, labResult]);

    const salvar = async () => {
        if (!pode('dados.alterar')) {
            setErro('Modo visitante: edição bloqueada.');
            return;
        }
        setSalvando(true);
        setErro('');
        // `parseValorLab` remove TODOS os pontos de milhar — ver utils/numeroLab.js.
        const payload = {
            experimentId,
            analysisDate: valores.analysisDate,
            bacterias: entradas.map((e) => ({
                bacteriaId: e.bacteriaId,
                bacteriaNome: e.bacteriaNome,
                nmp: parseValorLab(valores[e.chave]),
                unit: e.unit,
            })),
        };
        try {
            const salvo = labResult ? await updateLabResult(experimentId, payload) : await createLabResult(payload);
            onSalvo(normalizarResultadoLab(salvo));
        } catch (e) {
            setErro(e?.response?.data?.message || 'Não foi possível salvar o resultado.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={open} onClose={salvando ? undefined : onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
            <DialogTitle>{labResult ? 'Atualizar resultado de laboratório' : 'Adicionar resultado de laboratório'}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={{ xs: 2, sm: 3 }} sx={{ mt: 1 }}>
                    {/* Agrupado por bactéria: com duas unidades para a mesma bactéria (o
                        Efluente), campos soltos não deixariam claro o que é de quem. */}
                    {grupos.map((grupo) => (
                        <Box key={grupo.bacteriaId || grupo.bacteriaNome}>
                            {grupo.entradas.length > 1 && (
                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    {grupo.bacteriaNome}
                                </Typography>
                            )}
                            <Stack spacing={2}>
                                {grupo.entradas.map((e) => (
                                    <TextField
                                        key={e.chave}
                                        label={grupo.entradas.length > 1 ? e.unit : e.bacteriaNome}
                                        // `text`, não `number`: em `number` o navegador trata o
                                        // ponto como DECIMAL e "920.000" chegava como 920.
                                        type="text"
                                        // `decimal`, não `numeric`: no iOS o `numeric` esconde a vírgula.
                                        inputMode="decimal"
                                        value={valores[e.chave] ?? ''}
                                        onChange={(ev) => setValores((v) => ({ ...v, [e.chave]: formatarValorLab(ev.target.value) }))}
                                        fullWidth
                                        placeholder="0"
                                        helperText={unidadeComparavel(e.unit) ? undefined : 'Registrado, mas não entra na comparação com o modelo.'}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                                                        {e.unit}
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    ))}
                    <TextField
                        label="Data da análise no laboratório"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={valores.analysisDate}
                        onChange={(e) => setValores((v) => ({ ...v, analysisDate: e.target.value }))}
                        fullWidth
                    />
                    {erro && <Alert severity="error">{erro}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={salvando} color="inherit">Cancelar</Button>
                <Button onClick={salvar} variant="contained" disableElevation disabled={salvando}>
                    {salvando ? 'Salvando...' : labResult ? 'Atualizar' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
